import { useState, useEffect, useRef, useCallback } from "react";
import { FaTimes, FaCheck } from "react-icons/fa";
import NodataFound from "../nodata";
import { useAppContextProvider } from "../../contextapi";

const TrackChanges = (props) => {
  const [trackChange, setTrackchange] = useState([]);
  const [trackChangeData, setTrackchangeData] = useState([]);
  const [currentFilter, setCurrentFilter] = useState("all");
  const [currentTypeFilter, setCurrentTypeFilter] = useState("all");
  const [highlightedItem, setHighlightedItem] = useState(null);
  const {
    trackChanges,
    setTrackAction,
    setLineFocus,
    setSaveTrigger,
    userProfile,
    setSidebarClose,
    trackChangesEnabled,
    setTrackChangesEnabled,
    alttextEnabled,
  } = useAppContextProvider();
  const trackChangeRefs = useRef({});

  // Role-based filtered data
  const safeTrackChange = Array.isArray(trackChange) ? trackChange : [];

  const filteredByRole = currentFilter === "all"
    ? safeTrackChange
    : safeTrackChange.filter(item => item.role === currentFilter);

  const insertionCount = filteredByRole.filter(item => item.type === "insertion").length;
  const deletionCount = filteredByRole.filter(item => item.type === "deletion").length;
  const altTextCount = filteredByRole.filter(item => item?.altTextKey).length;

  // ✅ Count roles helper
  const getRoleCounts = (data) => {
    if (!Array.isArray(data)) return {};
    return data.reduce((acc, item) => {
      if (item && item.role) {
        acc[item.role] = (acc[item.role] || 0) + 1;
      }
      return acc;
    }, {});
  };

  const applyFilter = useCallback(
    (roleFilter, typeFilter, data) => {
      const source = Array.isArray(data) ? data : [];
      let filteredData = source;
      if (roleFilter !== "all") {
        filteredData = filteredData.filter((item) => item?.role === roleFilter);
      }
      if (typeFilter === "alttext") {
        filteredData = filteredData.filter((item) => !!item?.altTextKey);
      } else if (typeFilter !== "all") {
        filteredData = filteredData.filter((item) => item?.type === typeFilter);
      }
      setTrackchangeData(filteredData);
    },
    []
  );

  // Sync local `trackChange` with context `trackChanges` only
  useEffect(() => {
    if (Array.isArray(trackChanges) && trackChanges.length > 0) {
      setTrackchange(trackChanges);
    } else {
      setTrackchange([]);
    }
  }, [trackChanges]);

  // The Alt Text option only exists in the alt-text flow; drop back to
  // "All Types" if it was selected and the flow is off.
  useEffect(() => {
    if (!alttextEnabled && currentTypeFilter === "alttext") {
      setCurrentTypeFilter("all");
    }
  }, [alttextEnabled, currentTypeFilter]);

  // Apply filter when filter values or the local `trackChange` data change
  useEffect(() => {
    // Use the stable `applyFilter` and pass the current data explicitly
    applyFilter(currentFilter, currentTypeFilter, trackChange);
  }, [currentFilter, currentTypeFilter, trackChange, applyFilter]);

  // Handle editor-to-sidebar navigation
  useEffect(() => {
    const handleTrackChangeClick = (event) => {
      const trackChangeElement = event.target.closest(
        ".ol-cm-change-i, .ol-cm-change-d"
      );
      if (trackChangeElement) {
        const from =
          trackChangeElement.getAttribute("data-from") ||
          trackChangeElement.parentElement.getAttribute("data-from");
        const to =
          trackChangeElement.getAttribute("data-to") ||
          trackChangeElement.parentElement.getAttribute("data-to");
        const user =
          trackChangeElement.getAttribute("data-user") ||
          trackChangeElement.parentElement.getAttribute("data-user");
        const role =
          trackChangeElement.getAttribute("data-role") ||
          trackChangeElement.parentElement.getAttribute("data-role");

        if (from && to) {
          const matchingItem = trackChangeData.find(
            (item) =>
              item.from === parseInt(from) &&
              item.to === parseInt(to) &&
              item.Username === user &&
              item.role === role
          );

          if (matchingItem) {
            highlightTrackChange(matchingItem);
          }
        }
      }
    };

    document.addEventListener("click", handleTrackChangeClick);
    return () => {
      document.removeEventListener("click", handleTrackChangeClick);
    };
  }, [trackChangeData]);

  // Add a click handler to clear highlight when clicking outside track changes
  useEffect(() => {
    const handleOutsideClick = (event) => {
      // Check if click is outside both the editor track changes and the sidebar track changes
      const isEditorTrackChange = event.target.closest(
        ".ol-cm-change-i, .ol-cm-change-d"
      );
      const isSidebarTrackChange = event.target.closest(".track-group");

      if (!isEditorTrackChange && !isSidebarTrackChange && highlightedItem) {
        setHighlightedItem(null);
      }
    };

    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [highlightedItem]);

  const highlightTrackChange = (item) => {
    setHighlightedItem(item);

    // Scroll to the item in the sidebar only if it's not already visible
    const refKey = `${item.Username}-${item.role}-${item.from}-${item.to}`;
    if (item && trackChangeRefs.current[refKey]) {
      const element = trackChangeRefs.current[refKey];

      // Check if the element is already visible in the viewport
      const rect = element.getBoundingClientRect();
      const container = element.closest(".track-changes-content");
      const containerRect = container.getBoundingClientRect();

      const isVisible =
        rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;

      // Only scroll if the element is not fully visible
      if (!isVisible) {
        // Determine if we should scroll up or down
        if (rect.top < containerRect.top) {
          // Element is above the visible area, scroll it to top
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        } else if (rect.bottom > containerRect.bottom) {
          // Element is below the visible area, scroll it to bottom
          element.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }
      }
    }
  };

  const actionTrack = (type, id, item) => {
    setTrackAction({ status: type, list: item });
    const updatedData = [...trackChangeData];
    // Find the item by its unique properties rather than just index
    const itemIndex = updatedData.findIndex(
      (i) =>
        i.from === item.from &&
        i.to === item.to &&
        i.Username === item.Username &&
        i.role === item.role
    );
    if (itemIndex !== -1) {
      updatedData[itemIndex].status = type;
      setTrackchangeData(updatedData);
    }
    if (type === "Accept") {
      setSaveTrigger(true);
    }
  };

  const handleSelect = (e) => {
    const selectedFilter = e.target.value;
    setCurrentFilter(selectedFilter);
  };

  const handleTypeSelect = (e) => {
    const selectedTypeFilter = e.target.value;
    setCurrentTypeFilter(selectedTypeFilter);
  };

  const queryScroll = (item) => {
    setLineFocus(item);
    highlightTrackChange(item);
  };

  // Group changes by user and role
  const groupChangesByUser = (changes) => {
    const grouped = {};

    changes.forEach((item) => {
      // Create a unique key for each user+role combination
      const userKey = `${item.Username}-${item.role}`;

      if (!grouped[userKey]) {
        grouped[userKey] = [];
      }

      grouped[userKey].push(item);
    });

    return grouped;
  };

  // ✅ Role counts
  const roleCounts = getRoleCounts(trackChange);

  return (
    <div className="track-changes trackchangestab">
      <header className="slider-header">
        <h2>Track Changes - ({trackChangeData?.length})</h2>
        <button
          className="close-btn"
          title="Close"
          onClick={() => {
            setSidebarClose(true);
            setTimeout(() => {
              // Focus the first filter dropdown after closing
              const firstFilterDropdown = document.querySelector('.role-filter');
              if (firstFilterDropdown) firstFilterDropdown.focus();
            }, 0);
          }}
          onKeyDown={(e) => {
            if (e.key === "Tab" && !e.shiftKey) {
              e.preventDefault();
              setTimeout(() => {
                // Focus the first filter dropdown after closing
                const firstFilterDropdown = document.querySelector('.role-filter');
                if (firstFilterDropdown) firstFilterDropdown.focus();
              }, 0);
            }
          }}
        >
          <FaTimes />
        </button>
      </header>
      <main className="track-changes-content" aria-label="Track changes panel">
        {userProfile.role === "Copy Editor" && (
          <section>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <span
                style={{
                  padding: "2px 10px",
                  borderRadius: 8,
                  background: trackChangesEnabled ? "#e8f5e9" : "#fff3e0",
                  color: trackChangesEnabled ? "#388e3c" : "#b71c1c",
                  fontWeight: 600,
                  fontSize: 11,
                  whiteSpace: "nowrap",
                }}
              >
                {trackChangesEnabled
                  ? "Tracking is ON – all edits are tracked"
                  : "Tracking is OFF – edits are not tracked"}
              </span>
              {userProfile.role === "Copy Editor" && (
                <div
                  className="track-switch"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <input
                    id="trackSwitch"
                    type="checkbox"
                    checked={trackChangesEnabled}
                    onChange={(e) => setTrackChangesEnabled(e.target.checked)}
                  />
                </div>
              )}
            </div>
          </section>
        )}
        <section
          className="filterTop"
          style={{ display: "flex", gap: "4px", alignItems: "center" }}
        >
          {userProfile.role !== "Master Copier" ? (
            <p
              style={{
                fontSize: "12px",
                color: "#fff",
                letterSpacing: "0.3px",
              }}
            >
              Filter:
            </p>
          ) : (
            ""
          )}
          <select
            className="role-filter"
            aria-label="Role filter"
            value={currentFilter}
            onChange={(e) => handleSelect(e)}
          >
            {userProfile.role === "Master Copier" ? (
              <>
                <option value="all">All Roles</option>
                <option value="Author">
                  Author ({roleCounts["Author"] || 0})
                </option>
                <option value="Editor">
                  Editor ({roleCounts["Editor"] || 0})
                </option>
                <option value="Master Copier">
                  Master Copier ({roleCounts["Master Copier"] || 0})
                </option>
                <option value="Copy Editor">
                  Copy Editor ({roleCounts["Copy Editor"] || 0})
                </option>
              </>
            ) : (
              <>
                <option value="all">All Roles</option>
                <option value="Author">Author</option>
                <option value="Editor">Editor</option>
                <option value="Master Copier">Master Copier</option>
                <option value="Copy Editor">Copy Editor</option>
              </>
            )}
          </select>
          <select
            className="type-filter"
            aria-label="Type filter"
            value={currentTypeFilter}
            onChange={(e) => handleTypeSelect(e)}
          >
            {userProfile.role === "Master Copier" ? (
              <>
                <option value="all">All Types</option>
                <option value="insertion">Insertion ({insertionCount})</option>
                <option value="deletion">Deletion ({deletionCount})</option>
                {alttextEnabled && (
                  <option value="alttext">Alt Text ({altTextCount})</option>
                )}
              </>
            ) : (
              <>
                <option value="all">All Types</option>
                <option value="insertion">Insertion</option>
                <option value="deletion">Deletion</option>
                {alttextEnabled && <option value="alttext">Alt Text</option>}
              </>
            )}
          </select>
        </section>
        {trackChangeData?.length !== 0 ? (
          Object.entries(groupChangesByUser(trackChangeData)).map(
            ([userKey, userChanges]) => {
              // Extract user info from the key
              const [username, role] = userKey.split("-");

              return (
                <section key={userKey}>
                  {userChanges.map((item, i) => (
                    <article
                      className={`track-group ${highlightedItem?.from === item.from &&
                        highlightedItem?.to === item.to &&
                        highlightedItem?.Username === item.Username
                        ? "highlighted"
                        : ""
                        }`}
                      key={`${userKey}-${i}`}
                      onClick={() => queryScroll(item)}
                      ref={(el) =>
                      (trackChangeRefs.current[
                        `${item.Username}-${item.role}-${item.from}-${item.to}`
                      ] = el)
                      }
                    >
                      {item.type === "insertion" ? (
                        <label className="status insert-fixedContrast">
                          inserted
                        </label>
                      ) : (
                        <label className="status delete-fixedContrast">
                          deleted
                        </label>
                      )}
                      <h3
                        aria-label={`Change made by ${username} (${role}) on ${item.Date_Time}`}
                        tabIndex={0}
                      >
                        {role === "Copy Editor" ? role : username}
                        <span>{item.Date_Time}</span>
                      </h3>
                      <p
                        aria-label={`${item.text}`}
                        role="article"
                        tabIndex={0}
                        style={{ wordBreak: "break-word" }}
                      >
                        <label>Text:</label>
                        {item.text === undefined
                          ? item.decoration.spec.text
                          : item.text}
                      </p>
                      <footer className="trackfooter">
                        {item.role !== userProfile.role ? (
                          <button
                            className="accept-btn-clrContrast1"
                            onClick={(e) => {
                              e.stopPropagation();
                              actionTrack("Accept", i, item);
                            }}
                          >
                            <FaCheck /> Accept
                          </button>
                        ) : (
                          ""
                        )}
                        <button
                          className="reject-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            actionTrack("Reject", i, item);
                          }}
                          onKeyDown={e => {
                            if (
                              e.key === "Tab" &&
                              !e.shiftKey &&
                              userProfile.role === "Master Copier" &&
                              i === userChanges.length - 1
                            ) {
                              e.preventDefault();
                              setTimeout(() => {
                                const downloadBtn = document.querySelector('button[title="TrackChangeFile.tex download"]');
                                if (downloadBtn) downloadBtn.focus();
                              }, 0);
                            }
                          }}>
                          <FaTimes /> Reject
                        </button>
                      </footer>
                    </article>
                  ))}
                </section>
              );
            }
          )
        ) : (
          <NodataFound />
        )}
      </main>
    </div>
  );
};

export default TrackChanges;
